/* StoriesIndex — iter55 rebuild (Feb 2026)
 *
 * Cinematic Stories destination inspired by Apple TV+, Netflix, and The
 * Athletic. Rebuilt around athletes and long-form storytelling — no
 * homepage overlap, no ESPN feed, no sales widgets.
 *
 * The backend is untouched. This page composes existing endpoints:
 *   /api/stories                — sort=trending|most_saved|recent
 *   /api/stories?featured=true  — hero + Flyt Originals candidates
 *   /api/stories?coming_soon=true — Premieres
 *   /api/stories?since_days=7   — New This Week
 *   /api/collections            — Featured Collections
 *   /api/me/because-you-follow  — personalized recs (logged-in)
 *   /api/me/following           — Follow the Journey (athletes)
 *   dl_watch_history_v1 (local) — Continue Watching
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Play, BookOpen, ArrowRight, Clock, ChevronRight, Bell, Film,
  Layers, Search as SearchIcon, Sparkles,
} from 'lucide-react';
import { useChannel } from '../contexts/ChannelContext';
import { useUserAuth } from '../contexts/UserAuthContext';
import { resolveImageUrl } from '../components/ImageUpload';
import VideoModal from '../components/stories/VideoModal';
import { AppStoreBadge } from '../components/AppStorePromo';
import { openApplyModal } from '../components/ApplyToBeFeaturedModal';
// import StoriesScoresTicker from '../components/stories/StoriesScoresTicker'; // iter558 · removed with ESPN scores render

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ═════════════════════════════════════════════════════════════════════
 * Utilities
 * ═════════════════════════════════════════════════════════════════════ */

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');

/** Best-guess for a story's presentation mode. Falls back gracefully so
 *  articles without an explicit `content_format` field still render. */
const formatOf = (story) => {
  const f = (story?.content_format || story?.format || story?.media_type || '').toLowerCase();
  if (f) return f;
  if (story?.video_url || story?.trailer_url) return 'video';
  if (story?.duration_minutes) return 'video';
  return 'article';
};

const isWatchable = (story) => ['video', 'documentary', 'short_film', 'podcast'].includes(formatOf(story));

const durationLabel = (story) => {
  const m = story?.estimated_duration || story?.duration_minutes || story?.duration;
  if (!m) return '';
  const type = formatOf(story);
  return type === 'article' ? `${m} min read` : `${m} min`;
};

/* ═════════════════════════════════════════════════════════════════════
 * 1 · FEATURED STORY HERO
 * ═════════════════════════════════════════════════════════════════════ */

const HeroFeature = ({ story }) => {
  if (!story) return null;
  // iter55 · Hero always routes to the athlete's Network profile with a
  // "Watch Now" CTA — the athlete page is where the immersive experience
  // lives. Admin config chooses the athlete + hero video/image.
  const badge = story?.is_flyt_original ? 'FLYT ORIGINAL' : story?.breaking ? 'BREAKING' : story?.featured ? 'FEATURED' : 'NEW';
  const athleteHref = story.athlete_slug ? `/network/${story.athlete_slug}` : `/stories/${story.slug}`;
  // iter562 · Read Story always routes to the IN-APP story page.
  // Previously the admin `hero_read_story_url` override could point
  // at an external URL, which then opened Safari on native iOS —
  // Apple's guideline 4.7 dings apps that push users out to the web
  // for core content. We now derive the internal path from the
  // admin override when it points at /stories/… and ignore any
  // external URL entirely so the CTA never leaves the app.
  const _configured = (story.read_story_url || '').trim();
  const _internalMatch = _configured.match(/\/stories\/([^/?#]+)/i);
  const readHref = _internalMatch
    ? `/stories/${_internalMatch[1]}`
    : (story.athlete_slug && story.slug ? `/stories/${story.slug}` : '');

  return (
    <section className="relative overflow-hidden" data-testid="stories-hero">
      <div className="relative w-full aspect-[16/10] sm:aspect-[21/9] lg:aspect-[24/9] min-h-[440px]">
        {(story.hero_image || story.cover_image || story.thumbnail) ? (
          <img
            src={resolveImageUrl(story.hero_image || story.cover_image || story.thumbnail, 1200)}
            alt={story.name}
            className="absolute inset-0 w-full h-full object-cover"
            loading="eager"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-blue-950/60 via-black to-black" />
        )}
        {/* Cinematic gradient — heavy on bottom, softer on top */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/10" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-transparent" />

        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-10 pb-10 sm:pb-14">
            <div className="max-w-2xl">
              <p className="inline-block px-2.5 py-1 rounded-full bg-white/10 ring-1 ring-white/25 text-[10px] font-black uppercase tracking-[0.28em] text-white mb-4"
                 data-testid="stories-hero-badge">
                {badge}
              </p>
              {story.athlete_name && (
                <p className="font-display text-sm sm:text-base uppercase tracking-[0.32em] text-blue-300 mb-2">
                  {story.athlete_name}
                </p>
              )}
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] text-white">
                {story.name}
              </h1>
              {(story.description || story.excerpt) && (
                <p className="mt-4 text-white/75 text-base sm:text-lg leading-relaxed line-clamp-2 max-w-xl">
                  {story.description || story.excerpt}
                </p>
              )}
              <p className="mt-3 text-[11px] uppercase tracking-widest text-white/50 font-bold">
                Documentary · Network Profile
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-2">
                <Link
                  to={athleteHref}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-black text-[11px] font-black uppercase tracking-widest hover:bg-blue-200 transition"
                  data-testid="stories-hero-primary-cta"
                >
                  <Play className="w-4 h-4" /> Watch Now
                </Link>
                {story.athlete_slug && story.slug && (
                  <Link
                    to={readHref}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full ring-1 ring-white/25 bg-white/5 hover:bg-white/15 text-white text-[11px] font-black uppercase tracking-widest transition"
                    data-testid="stories-hero-story-cta"
                  >
                    Read story <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
                {/* iter561 · Bookmark/Save button removed — the save
                    action wasn't wired to anything, so it was a dead
                    tap. Left the container so Watch Now + Read Story
                    can breathe without the empty third slot. */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ═════════════════════════════════════════════════════════════════════
 * 2 · WATCH NOW — admin-picked video rail (replaces Continue Watching).
 *
 * Reads `originals_items` from /api/stories-layout. Each card is an
 * admin-configured athlete + video pair; tap plays inline via
 * VideoModal. The founder controls the rail entirely from
 * Admin → Stories Page Layout.
 * ═════════════════════════════════════════════════════════════════════ */

const WatchNowRail = ({ cfg, onPlay }) => {
  const items = Array.isArray(cfg?.originals_items) ? cfg.originals_items : [];
  if (!cfg || items.length === 0) return null;

  const kicker = cfg.watch_rail_kicker || 'Tap to play';
  const title  = cfg.watch_rail_title  || 'Watch Now';

  return (
    <section className="py-10" data-testid="stories-watch-now">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="font-italic text-[11px] uppercase tracking-[0.22em] text-blue-300 mb-1">{kicker}</p>
        <h2 className="font-display text-2xl sm:text-3xl uppercase leading-none text-white mb-5">{title}</h2>
        <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3" style={{ scrollbarWidth: 'none' }}>
          {items.map((it, i) => {
            const thumb = it.thumbnail || it.photo || it.cover_image;
            const label = it.video_title || it.name || 'Watch';
            const badge = it.badge || 'FLYT ORIGINAL';
            // Build a story-shaped blob so VideoModal (which reads
            // video_url/name/athlete_slug/etc.) can render it as-is.
            const storyBlob = {
              slug: `watch-${it.slug || i}`,
              name: it.video_title || it.name || 'Watch Now',
              description: it.video_description || '',
              athlete_slug: it.slug,
              athlete_name: it.name,
              thumbnail: thumb,
              cover_image: thumb,
              video_url: it.video_url,
              category: it.badge || 'FLYT Original',
            };
            const canPlay = !!it.video_url;
            return (
              <button
                key={`${it.slug || 'w'}-${i}`}
                type="button"
                onClick={() => (canPlay ? onPlay?.(storyBlob) : null)}
                disabled={!canPlay}
                className="snap-start flex-shrink-0 w-[70vw] sm:w-[42vw] md:w-[300px] lg:w-[320px] group text-left disabled:cursor-not-allowed"
                data-testid={`stories-watch-${it.slug || i}`}
              >
                <div className="relative aspect-[16/10] rounded-2xl overflow-hidden bg-white/[0.04] ring-1 ring-white/10">
                  {thumb ? (
                    <img
                      src={resolveImageUrl(thumb, 400)}
                      alt={label}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-purple-900/40 to-black" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                  <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 ring-1 ring-white/15 text-white/85 text-[9px] uppercase tracking-widest font-bold">
                    {badge}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <p className="text-[10px] uppercase tracking-widest text-blue-300 font-bold">{it.name}</p>
                    <p className="font-display text-lg text-white uppercase leading-tight line-clamp-2 mt-0.5">{label}</p>
                    {it.video_description && (
                      <p className="text-white/60 text-[11px] mt-1 line-clamp-2">{it.video_description}</p>
                    )}
                  </div>
                  {canPlay && (
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                      <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-2xl">
                        <Play className="w-6 h-6 text-black translate-x-0.5" />
                      </div>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ═════════════════════════════════════════════════════════════════════
 * 3 · TRAILERS — admin-managed coming-soon rail (iter263).
 * Reads `trailer_items` from /api/stories-layout. Each card is a
 * founder-added Vimeo link + thumbnail + title/description managed in
 * Admin → Stories Page Layout. Tap → VideoModal pop-up.
 * ═════════════════════════════════════════════════════════════════════ */

const TrailersRail = ({ cfg, onPlay }) => {
  const items = (Array.isArray(cfg?.trailer_items) ? cfg.trailer_items : [])
    .filter((t) => (t?.title || '').trim() || (t?.vimeo_url || '').trim());
  if (items.length === 0) return null;

  return (
    <section className="py-10" data-testid="stories-trailers">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="font-italic text-[11px] uppercase tracking-[0.22em] text-blue-300 mb-1">Coming soon</p>
        <h2 className="font-display text-2xl sm:text-3xl uppercase leading-none text-white mb-5">Trailers</h2>
        <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3" style={{ scrollbarWidth: 'none' }}>
          {items.map((t, i) => {
            const storyBlob = {
              slug: `trailer-${t.id || i}`,
              name: t.title || 'Trailer',
              description: t.description || '',
              thumbnail: t.thumbnail,
              cover_image: t.thumbnail,
              video_url: t.vimeo_url,
              category: 'Trailer',
            };
            return (
              <button
                key={t.id || i}
                type="button"
                onClick={() => onPlay?.(storyBlob)}
                className="snap-start flex-shrink-0 w-[70vw] sm:w-[42vw] md:w-[300px] lg:w-[320px] group text-left"
                data-testid={`stories-trailer-${i}`}
              >
                <div className="relative aspect-[16/10] rounded-2xl overflow-hidden bg-white/[0.04] ring-1 ring-white/10">
                  {t.thumbnail ? (
                    <img
                      src={resolveImageUrl(t.thumbnail, 400)}
                      alt={t.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-blue-900/40 to-black" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                  <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-500/90 text-white text-[9px] uppercase tracking-widest font-black">
                    Coming soon
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <p className="font-display text-lg text-white uppercase leading-tight line-clamp-2">{t.title}</p>
                    {t.description && (
                      <p className="text-white/60 text-[11px] mt-1 line-clamp-2">{t.description}</p>
                    )}
                  </div>
                  {t.vimeo_url && (
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                      <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-2xl">
                        <Play className="w-6 h-6 text-black translate-x-0.5" />
                      </div>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ═════════════════════════════════════════════════════════════════════
 * 5 · FOLLOW THE JOURNEY — athlete-led collection cards
 * ═════════════════════════════════════════════════════════════════════ */

const FollowTheJourney = ({ channelId }) => {
  const [athletes, setAthletes] = useState([]);

  useEffect(() => {
    // Pull the same "featured stories" list and reduce to unique athletes.
    // Every athlete card carries their most-recent story and the total
    // story count so a click is a promise of depth.
    const run = async () => {
      try {
        const { data } = await axios.get(`${API}/stories?channel=${channelId}&limit=48`);
        const map = new Map();
        (data || []).forEach((s) => {
          if (!s?.athlete_slug) return;
          if (!map.has(s.athlete_slug)) {
            map.set(s.athlete_slug, {
              slug: s.athlete_slug,
              name: s.athlete_name || s.author || 'Athlete',
              sport: s.sport || s.category,
              team: s.team || s.school_name || '',
              hero: s.hero_image || s.thumbnail || s.cover_image,
              latestTitle: s.name,
              stories: 1,
            });
          } else {
            const cur = map.get(s.athlete_slug);
            cur.stories += 1;
          }
        });
        setAthletes(Array.from(map.values()).filter((a) => a.stories >= 1).slice(0, 12));
      } catch { setAthletes([]); }
    };
    run();
  }, [channelId]);

  if (athletes.length === 0) return null;

  return (
    <section className="py-10" data-testid="stories-follow-journey">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-5">
          <div>
            <p className="font-italic text-[11px] uppercase tracking-[0.22em] text-blue-300 mb-1">One athlete, every chapter</p>
            <h2 className="font-display text-2xl sm:text-3xl uppercase leading-none text-white">Follow the Journey</h2>
          </div>
        </div>
        <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3" style={{ scrollbarWidth: 'none' }}>
          {athletes.map((a) => (
            <Link
              key={a.slug}
              to={`/network/${a.slug}`}
              className="group snap-start flex-shrink-0 w-[62vw] sm:w-[38vw] md:w-[260px] lg:w-[280px]"
              data-testid={`stories-journey-${a.slug}`}
            >
              <div className="relative aspect-[3/4] rounded-2xl overflow-hidden ring-1 ring-white/10 bg-white/[0.03]">
                <img src={resolveImageUrl(a.hero, 400)} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-3.5">
                  <p className="text-[10px] uppercase tracking-widest text-blue-300 font-bold mb-0.5">{a.stories} {a.stories === 1 ? 'story' : 'stories'}</p>
                  <p className="font-display text-lg text-white uppercase leading-tight">{a.name}</p>
                  {(a.sport || a.team) && (
                    <p className="text-[11px] text-white/60 mt-0.5 truncate">
                      {[a.sport, a.team].filter(Boolean).join(' · ')}
                    </p>
                  )}
                  <p className="text-[11px] text-white/70 mt-2 line-clamp-2">Latest: {a.latestTitle}</p>
                  <p className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-white font-bold">
                    View journey <ArrowRight className="w-3 h-3" />
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ═════════════════════════════════════════════════════════════════════
 * 6 · FEATURED COLLECTIONS
 * ═════════════════════════════════════════════════════════════════════ */

const FeaturedCollections = ({ channelId }) => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios.get(`${API}/collections?channel=${channelId}&featured=true&limit=12`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  }, [channelId]);

  if (items.length === 0) return null;

  return (
    <section className="py-10" data-testid="stories-collections">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-5">
          <div>
            <p className="font-italic text-[11px] uppercase tracking-[0.22em] text-blue-300 mb-1">Themed for binge</p>
            <h2 className="font-display text-2xl sm:text-3xl uppercase leading-none text-white">Featured Collections</h2>
          </div>
          <Link to="/collections" className="hidden md:inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-white/70 hover:text-white">
            See all <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3" style={{ scrollbarWidth: 'none' }}>
          {items.map((c) => (
            <Link
              key={c.id || c.slug}
              to={`/collections/${c.slug}`}
              className="group snap-start flex-shrink-0 w-[70vw] sm:w-[40vw] md:w-[300px] lg:w-[320px]"
              data-testid={`stories-collection-${c.slug}`}
            >
              <div className="relative aspect-[4/5] rounded-2xl overflow-hidden ring-1 ring-white/10 bg-white/[0.03]">
                <img src={resolveImageUrl(c.cover_image || c.thumbnail, 400)} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/60 ring-1 ring-white/20 text-white/90 text-[9px] uppercase tracking-widest font-bold">
                  <Layers className="w-3 h-3" /> Collection
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="font-display text-xl text-white uppercase leading-tight">{c.name}</h3>
                  {c.description && (
                    <p className="text-white/70 text-[12px] mt-1 line-clamp-2">{c.description}</p>
                  )}
                  <p className="text-white/50 text-[11px] mt-2">{c.story_count || (c.story_slugs || []).length || '—'} stories</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ═════════════════════════════════════════════════════════════════════
 * 8 · PREMIERES — coming-soon stories with countdown + Notify Me
 * ═════════════════════════════════════════════════════════════════════ */

const PremieresRow = ({ channelId }) => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios.get(`${API}/stories?coming_soon=true&channel=${channelId}&limit=8`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  }, [channelId]);

  if (items.length === 0) return null;

  return (
    <section className="py-10" data-testid="stories-premieres">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="font-italic text-[11px] uppercase tracking-[0.22em] text-blue-300 mb-1">On the calendar</p>
        <h2 className="font-display text-2xl sm:text-3xl uppercase leading-none text-white mb-5">Premieres</h2>
        <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3" style={{ scrollbarWidth: 'none' }}>
          {items.map((s) => {
            const releaseAt = s.premiere_at || s.publish_date;
            const isLive = releaseAt && new Date(releaseAt).getTime() <= Date.now();
            return (
              <Link
                key={s.id}
                to={`/stories/${s.slug}`}
                className="group snap-start flex-shrink-0 w-[70vw] sm:w-[40vw] md:w-[300px] lg:w-[320px]"
                data-testid={`stories-premiere-${s.slug}`}
              >
                <div className="relative aspect-[4/5] rounded-2xl overflow-hidden ring-1 ring-white/10 bg-white/[0.03]">
                  <img src={resolveImageUrl(s.thumbnail || s.cover_image, 400)} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                  <div className="absolute top-3 left-3 px-2 py-1 rounded-full bg-blue-500/90 text-white text-[9px] uppercase tracking-widest font-black">
                    {isLive ? 'Available now' : 'Premiere'}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-3.5">
                    <p className="text-[10px] uppercase tracking-widest text-white/60 mb-0.5">{s.athlete_name || s.author}</p>
                    <h3 className="font-display text-lg text-white uppercase leading-tight line-clamp-2">{s.name}</h3>
                    <p className="text-[11px] text-white/60 mt-1">{fmtDate(releaseAt)}</p>
                    <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-black text-[10px] font-black uppercase tracking-widest">
                      {isLive
                        ? (<>{isWatchable(s) ? <Play className="w-3 h-3" /> : <BookOpen className="w-3 h-3" />} {isWatchable(s) ? 'Watch' : 'Read'}</>)
                        : (<><Bell className="w-3 h-3" /> Notify me</>)
                      }
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ═════════════════════════════════════════════════════════════════════
 * 10 · LATEST STORIES — filtered library
 * ═════════════════════════════════════════════════════════════════════ */

const FORMATS = [
  { key: 'all',           label: 'All' },
  { key: 'documentary',   label: 'Documentaries' },
  { key: 'interview',     label: 'Interviews' },
  { key: 'article',       label: 'Articles' },
  { key: 'short_film',    label: 'Short Films' },
];
const SORTS = [
  { key: 'recent',        label: 'Newest' },
  { key: 'trending',      label: 'Most Popular' },
  { key: 'most_watched',  label: 'Most Watched' },
  { key: 'most_saved',    label: 'Most Saved' },
];

const LatestLibrary = ({ channelId }) => {
  const [format, setFormat] = useState('all');
  const [sort, setSort] = useState('recent');
  const [visible, setVisible] = useState(12);
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios.get(`${API}/stories?channel=${channelId}&sort=${sort}&limit=60`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
    setVisible(12);
  }, [channelId, sort]);

  const filtered = useMemo(() => {
    if (format === 'all') return items;
    return items.filter((s) => formatOf(s) === format);
  }, [items, format]);

  const shown = filtered.slice(0, visible);

  return (
    <section className="py-10" data-testid="stories-latest">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <p className="font-italic text-[11px] uppercase tracking-[0.22em] text-blue-300 mb-1">The full library</p>
          <h2 className="font-display text-2xl sm:text-3xl uppercase leading-none text-white">Latest Stories</h2>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1" data-testid="stories-latest-filters">
            {FORMATS.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFormat(f.key)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-widest font-bold transition ring-1 ${
                  format === f.key
                    ? 'bg-white text-black ring-white'
                    : 'bg-white/5 text-white/70 ring-white/10 hover:bg-white/10 hover:text-white'
                }`}
                data-testid={`stories-latest-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <p className="text-[10px] uppercase tracking-widest text-white/40 font-bold">Sort</p>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-black/40 ring-1 ring-white/10 rounded-full px-3 py-1.5 text-[11px] uppercase tracking-widest text-white outline-none"
              data-testid="stories-latest-sort"
            >
              {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
        </div>

        {shown.length === 0 ? (
          <p className="text-white/40 text-sm py-10 text-center" data-testid="stories-latest-empty">Nothing here yet.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {shown.map((s) => {
              const watch = isWatchable(s);
              return (
                <Link
                  key={s.id}
                  to={`/stories/${s.slug}`}
                  className="group"
                  data-testid={`stories-latest-card-${s.slug}`}
                >
                  <div className="relative aspect-[4/5] rounded-2xl overflow-hidden ring-1 ring-white/10 bg-white/[0.03]">
                    <img src={resolveImageUrl(s.thumbnail || s.cover_image, 400)} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 ring-1 ring-white/15 text-white/85 text-[9px] uppercase tracking-widest font-bold">
                      {watch ? <Play className="w-2.5 h-2.5" /> : <BookOpen className="w-2.5 h-2.5" />} {formatOf(s)}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <p className="text-[9px] uppercase tracking-widest text-blue-300 font-bold">{s.category}</p>
                      <h3 className="font-display text-base text-white uppercase leading-tight line-clamp-2 mt-0.5">{s.name}</h3>
                      <p className="text-[10px] text-white/50 mt-1 flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5" /> {durationLabel(s) || fmtDate(s.publish_date)}
                      </p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {filtered.length > visible && (
          <div className="text-center pt-8">
            <button
              type="button"
              onClick={() => setVisible((v) => v + 12)}
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full ring-1 ring-white/15 bg-white/5 hover:bg-white/15 text-white text-[11px] font-bold uppercase tracking-widest transition"
              data-testid="stories-latest-more"
            >
              Load more <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

/* ═════════════════════════════════════════════════════════════════════
 * 11 · FINAL CTA
 * ═════════════════════════════════════════════════════════════════════ */

const FinalCTA = () => {
  const { user } = useUserAuth();
  return (
    <section className="py-16 sm:py-20" data-testid="stories-final-cta">
      <div className="max-w-4xl mx-auto text-center px-4 sm:px-6">
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl uppercase leading-[0.98] text-white">
          Go deeper than the highlights.
        </h2>
        <p className="mt-4 text-white/60 text-base sm:text-lg max-w-2xl mx-auto">
          {user
            ? 'Follow more athletes, save stories for later, and get notified when new chapters drop.'
            : 'Follow athletes, save stories, and get notified when new chapters drop.'}
        </p>
        <div className="mt-8">
          {user ? (
            <button
              type="button"
              onClick={openApplyModal}
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-black text-[11px] font-black uppercase tracking-widest hover:bg-blue-200 transition"
              data-testid="stories-final-cta-link"
            >
              <Sparkles className="w-4 h-4" /> Apply for a story
            </button>
          ) : (
            <Link
              to="/signup?from=stories"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-black text-[11px] font-black uppercase tracking-widest hover:bg-blue-200 transition"
              data-testid="stories-final-cta-link"
            >
              <SearchIcon className="w-4 h-4" /> Create free account
            </Link>
          )}
          <div className="mt-5 flex justify-center">
            <AppStoreBadge variant="light" size="sm" testid="stories-app-store-badge" />
          </div>
        </div>
      </div>
    </section>
  );
};

/* ═════════════════════════════════════════════════════════════════════
 * PAGE SLOTS — founder-authored HTML injected between story rows.
 *
 * Anchors match the `SLOT_PLACEMENTS` list in StoriesPageLayoutTab.js.
 * We render raw HTML via dangerouslySetInnerHTML — trust boundary is
 * the admin panel (only founders/admins can write to `page_slots`).
 * ═════════════════════════════════════════════════════════════════════ */

const PageSlots = ({ slots, at }) => {
  const list = (slots || []).filter((s) => s?.placement === at && (s.html || '').trim());
  if (list.length === 0) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid={`stories-slots-${at}`}>
      <div className="space-y-4">
        {list.map((s, i) => (
          <div
            key={s.id || `${at}-${i}`}
            className="stories-custom-slot text-white/85"
            data-testid={`stories-slot-${at}-${i}`}
            dangerouslySetInnerHTML={{ __html: s.html }}
          />
        ))}
      </div>
    </section>
  );
};

/* ═════════════════════════════════════════════════════════════════════
 * PAGE
 * ═════════════════════════════════════════════════════════════════════ */

const StoriesIndex = () => {
  const channel = useChannel();
  const { user } = useUserAuth();
  const channelId = channel.id;
  const [hero, setHero] = useState(null);
  // iter55c · Shared layout config so both the Hero and the new
  // Watch Now video rail read from the same admin-controlled source.
  const [layoutCfg, setLayoutCfg] = useState(null);

  // Featured hero — pulls layout config first (admin-editable athlete +
  // Vimeo hero video), then falls back to newest featured story.
  useEffect(() => {
    const run = async () => {
      try {
        const cfg = await axios.get(`${API}/stories-layout`).then((r) => r.data).catch(() => null);
        setLayoutCfg(cfg || null);
        // iter354 · Only trust the configured hero when it actually has a
        // visual (image or video). A hero_athlete_slug with an EMPTY
        // hero_image used to short-circuit here and render a broken <img>.
        if (cfg?.hero_athlete_slug && (cfg.hero_image || cfg.hero_video_url)) {
          setHero({
            slug: `hero-${cfg.hero_athlete_slug}`,
            name: cfg.hero_title || `${cfg.hero_athlete_name || 'Featured Athlete'}`,
            description: cfg.hero_description,
            athlete_name: cfg.hero_athlete_name,
            athlete_slug: cfg.hero_athlete_slug,
            hero_image: cfg.hero_image,
            video_url: cfg.hero_video_url,
            read_story_url: cfg.hero_read_story_url || '',
            is_flyt_original: (cfg.hero_badge || '').toUpperCase().includes('FLYT'),
            featured: true,
          });
          return;
        }
        const [feat, latest] = await Promise.all([
          axios.get(`${API}/stories?featured=true&channel=${channelId}&limit=1`),
          axios.get(`${API}/stories?channel=${channelId}&limit=1`),
        ]);
        setHero((feat.data && feat.data[0]) || (latest.data && latest.data[0]) || null);
      } catch { setHero(null); }
    };
    run();
  }, [channelId]);

  // iter55b · Modal-driven Watch experience for Continue Watching and
  // FLYT Originals. `activeVideo` holds the currently-playing story.
  const [activeVideo, setActiveVideo] = useState(null);

  return (
    <div className="min-h-screen bg-neutral-950 text-white" data-testid="stories-page">
      {/* iter558 · ESPN live-scores ticker removed — App Store rejects
          apps that redistribute live scores without a license. */}

      {/* 1 · Hero */}
      <HeroFeature story={hero} />
      <PageSlots slots={layoutCfg?.page_slots} at="after_hero" />

      {/* 2 · Watch Now rail — admin-curated video cards (tap → popup) */}
      <WatchNowRail cfg={layoutCfg} onPlay={setActiveVideo} />
      <PageSlots slots={layoutCfg?.page_slots} at="after_watch_now" />

      {/* 2b · Continue Watching (logged-in + progress only) */}
      {/* iter239 · Continue Watching removed per Jay — no watch content yet; re-add later. */}

      {/* 3 · Trailers — admin-managed coming-soon rail (iter263).
          The old auto-fetched coming_soon stories row is retired —
          the founder now adds each trailer (Vimeo + thumbnail) in
          Admin → Stories Page Layout. */}
      <TrailersRail cfg={layoutCfg} onPlay={setActiveVideo} />
      <PageSlots slots={layoutCfg?.page_slots} at="after_new_this_week" />

      {/* 4 · FLYT Originals rail REMOVED (iter115) — the admin-curated
          Watch Now rail above IS the FLYT Originals list now, no need
          for a duplicate auto-fetched row. `after_flyt_originals` slot
          anchor kept live so old slot data still renders. */}
      <PageSlots slots={layoutCfg?.page_slots} at="after_flyt_originals" />

      {/* 5 · Follow the Journey — athlete-led */}
      <FollowTheJourney channelId={channelId} />
      <PageSlots slots={layoutCfg?.page_slots} at="after_follow_journey" />

      {/* 6 · Premieres rail REMOVED (iter115) — Trailers rail above
          covers coming-soon content. Slot anchor kept live. */}
      <PageSlots slots={layoutCfg?.page_slots} at="after_premieres" />

      {/* 7 · Latest — full library with filters */}
      <LatestLibrary channelId={channelId} />
      <PageSlots slots={layoutCfg?.page_slots} at="after_latest" />

      {/* 8 · Final CTA */}
      <FinalCTA />

      {/* Video modal — pops in-page for Continue Watching + FLYT Originals */}
      {activeVideo && <VideoModal story={activeVideo} onClose={() => setActiveVideo(null)} />}
    </div>
  );
};

export default StoriesIndex;
